import React, { FC, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Modal from 'components/Modal/Modal';
import { ModalWidthEnum } from 'components/Modal/Modal.types';
import { Spinner } from 'components/Spinner/Spinner';
import { Text } from 'components/Typography';
import { Colors } from 'core/CssVariables';
import { FaqCategory } from 'data/types/faq.type';
import {
  useGetFaqIndexQuery,
  useLazyGetFaqContentQuery,
} from 'redux/apiSlices/faq.slice';
import {
  SFaqContent,
  SFaqWrapper,
  SFaqCategoryList,
  SFaqCategoryItem,
  SFaqModalOverride,
  SLoadingContainer,
} from './FaqModal.style';

interface FaqModalProps {
  visible: boolean;
  onClose: () => void;
}

const FaqModal: FC<FaqModalProps> = ({ visible, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory | null>(
    null,
  );

  const { data: faqIndex, isLoading: isIndexLoading } = useGetFaqIndexQuery(
    undefined,
    { skip: !visible },
  );
  const [
    fetchFaqContent,
    { data: markdown, isFetching: isContentFetching, error: fetchError },
  ] = useLazyGetFaqContentQuery();

  const sortedCategories = useMemo(
    () =>
      faqIndex?.categories
        ? [...faqIndex.categories].sort((a, b) => a.order - b.order)
        : [],
    [faqIndex?.categories],
  );

  useEffect(() => {
    if (visible && sortedCategories.length && !selectedCategory) {
      setSelectedCategory(sortedCategories[0]);
      fetchFaqContent(sortedCategories[0].id);
    }
  }, [visible, sortedCategories, selectedCategory, fetchFaqContent]);

  const handleClose = () => {
    setSelectedCategory(null);
    onClose();
  };

  const handleCategorySelect = (category: FaqCategory) => {
    setSelectedCategory(category);
    fetchFaqContent(category.id);
  };

  const renderContent = () => {
    if (isContentFetching) {
      return (
        <SLoadingContainer>
          <Spinner />
        </SLoadingContainer>
      );
    }

    if (fetchError) {
      return (
        <Text fontlevel={5} color={Colors.ErrorColor}>
          Failed to load FAQ content
        </Text>
      );
    }

    if (!markdown) {
      return (
        <Text fontlevel={5} color={Colors.SecondaryColor}>
          Select a category to view content
        </Text>
      );
    }

    // Note: react-markdown v8+ is XSS-safe by default as it doesn't render raw HTML.
    // Do NOT add rehype-raw plugin without proper sanitization.
    return <ReactMarkdown>{markdown}</ReactMarkdown>;
  };

  return (
    <>
      {visible && <SFaqModalOverride />}
      <Modal
        visible={visible}
        title="Frequently Asked Questions"
        onCancel={handleClose}
        onOk={handleClose}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        size={ModalWidthEnum.ExtraLarge}
        destroyOnClose
        className="faq-modal"
      >
        {isIndexLoading && (
          <SLoadingContainer>
            <Spinner />
          </SLoadingContainer>
        )}
        {!isIndexLoading && sortedCategories.length === 0 && (
          <Text fontlevel={5} color={Colors.SecondaryColor}>
            No FAQ articles available
          </Text>
        )}
        {!isIndexLoading && sortedCategories.length > 0 && (
          <SFaqWrapper>
            <SFaqCategoryList>
              <div className="category-list-title">Categories</div>
              {sortedCategories.map(category => (
                <SFaqCategoryItem
                  key={category.id}
                  $isActive={selectedCategory?.id === category.id}
                  onClick={() => handleCategorySelect(category)}
                >
                  <div className="title">{category.title}</div>
                </SFaqCategoryItem>
              ))}
            </SFaqCategoryList>
            <SFaqContent>{renderContent()}</SFaqContent>
          </SFaqWrapper>
        )}
      </Modal>
    </>
  );
};

export default FaqModal;
